import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import "./index.css";

const API_BASE = process.env.REACT_APP_API_URL;

const STATUS_LABELS = {
  BID: "Bid",
  SCH: "Scheduled",
  WRK: "In progress",
  CMP: "Completed",
  INV: "Invoiced",
  CAN: "Cancelled",
};

const getStatusLabel = (status) => STATUS_LABELS[status] || status || "Unknown";

const getPrimaryInvoice = (job) => {
  if (!job?.invoices?.length) {
    return null;
  }

  return (
    job.invoices.find((invoice) => invoice.invoice_type === "EST") ||
    job.invoices.find((invoice) => invoice.invoice_type === "INV") ||
    job.invoices[0]
  );
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  return `$${Number(value).toFixed(2)}`;
};

const PortalPage = () => {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const deferredSearch = useDeferredValue(searchTerm);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    const headers = new Headers({
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    });

    fetch(`${API_BASE}/api/jobs/`, {
      method: "GET",
      headers,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Unable to load portal jobs.");
        }
        return response.json();
      })
      .then((data) => {
        setJobs(Array.isArray(data) ? data : []);
      })
      .catch((fetchError) => {
        setError(fetchError.message || "Unable to load portal jobs.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const filteredJobs = useMemo(() => {
    const normalizedSearch = deferredSearch.trim().toLowerCase();

    if (!normalizedSearch) {
      return jobs;
    }

    return jobs.filter((job) => {
      const haystack = [
        job.job_number,
        job.job_site_address,
        job.customer_reported,
        job.bill_to_name,
        job.client?.name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [deferredSearch, jobs]);

  const summary = useMemo(() => {
    const activeJobs = jobs.filter((job) => !["CMP", "CAN"].includes(job.status)).length;
    const openInvoices = jobs.filter((job) =>
      (job.invoices || []).some((invoice) => invoice.status !== "Paid")
    ).length;

    return {
      total: jobs.length,
      active: activeJobs,
      openInvoices,
    };
  }, [jobs]);

  return (
    <div className="portal-page">
      <div className="container-responsive">
        <section className="portal-hero">
          <div className="portal-hero__copy">
            <p className="portal-hero__eyebrow">Customer portal</p>
            <h1>Find jobs, estimates, and invoices without guessing where to click.</h1>
            <p>
              Use the search below to find a project by job number, customer name, address,
              or customer-reported notes. From here you can jump into the main job board or
              open the related invoice directly.
            </p>
          </div>
          <div className="portal-summary">
            <div className="portal-summary__card">
              <span>Total jobs</span>
              <strong>{summary.total}</strong>
            </div>
            <div className="portal-summary__card">
              <span>Active jobs</span>
              <strong>{summary.active}</strong>
            </div>
            <div className="portal-summary__card">
              <span>Jobs with open invoices</span>
              <strong>{summary.openInvoices}</strong>
            </div>
          </div>
        </section>

        <section className="portal-toolbar">
          <label className="portal-search">
            <span>Search jobs</span>
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Try a job number, address, customer name, or note"
            />
          </label>
          <div className="portal-toolbar__hint">
            <strong>{filteredJobs.length}</strong>
            <span>{filteredJobs.length === 1 ? "result" : "results"}</span>
          </div>
        </section>

        {isLoading ? <div className="portal-state">Loading portal jobs…</div> : null}
        {!isLoading && error ? <div className="portal-state portal-state--error">{error}</div> : null}

        {!isLoading && !error ? (
          <section className="portal-results">
            {filteredJobs.length ? (
              filteredJobs.map((job) => {
                const primaryInvoice = getPrimaryInvoice(job);

                return (
                  <article key={job.id} className="portal-card">
                    <div className="portal-card__top">
                      <div>
                        <p className="portal-card__kicker">Job {job.job_number || "Unassigned"}</p>
                        <h2>{job.job_site_address || "No job address on file"}</h2>
                      </div>
                      <span className={`portal-status portal-status--${(job.status || "UNK").toLowerCase()}`}>
                        {getStatusLabel(job.status)}
                      </span>
                    </div>

                    <div className="portal-card__grid">
                      <div>
                        <span className="portal-card__label">Customer</span>
                        <p>{job.bill_to_name || job.client?.name || "Not provided"}</p>
                      </div>
                      <div>
                        <span className="portal-card__label">Reported issue</span>
                        <p>{job.customer_reported || "No issue description recorded."}</p>
                      </div>
                      <div>
                        <span className="portal-card__label">Primary invoice</span>
                        <p>
                          {primaryInvoice
                            ? `${primaryInvoice.invoice_type} · ${formatCurrency(primaryInvoice.amount_due)}`
                            : "No invoice available yet"}
                        </p>
                      </div>
                      <div>
                        <span className="portal-card__label">Billing contact</span>
                        <p>{job.bill_to_email_address || job.bill_to_phone_number || "Not provided"}</p>
                      </div>
                    </div>

                    <div className="portal-card__actions">
                      <a className="portal-button portal-button--primary" href={`/#job${job.id}`}>
                        Open on job board
                      </a>
                      {primaryInvoice ? (
                        <a
                          className="portal-button portal-button--secondary"
                          href={`/invoice/${primaryInvoice.guid}`}
                        >
                          Open invoice
                        </a>
                      ) : null}
                    </div>
                  </article>
                );
              })
            ) : (
              <div className="portal-state">No jobs matched your search.</div>
            )}
          </section>
        ) : null}
      </div>
    </div>
  );
};

export default PortalPage;
